/**
 * Login page wiring. Deliberately uses a standalone `fetch()` call, not
 * `lib/api.ts`'s `fetchJson` — `fetchJson`'s 401-redirect behavior (see
 * `lib/api.ts`) would be actively wrong here: a failed login *is* the
 * expected 401, not a signal to bounce to `/login` — that's exactly where
 * the user already is. See docs/technical-plan-prism-auth.md §9.1/§9.2.
 */
const form = document.querySelector<HTMLFormElement>("#login-form");
const emailInput = document.querySelector<HTMLInputElement>("#email");
const passwordInput = document.querySelector<HTMLInputElement>("#password");
const loginButton = document.querySelector<HTMLButtonElement>("#login-button");
const errorEl = document.querySelector<HTMLParagraphElement>("#login-error");
const forgotButton = document.querySelector<HTMLButtonElement>("#forgot-password-button");
const forgotHint = document.querySelector<HTMLParagraphElement>("#forgot-password-hint");

// Purely static, honest hint — no network call, no navigation, no actual
// reset flow. Prism has no self-service password reset; this mirrors
// DrillsDB's own login screen's "contact your administrator" pattern
// (`forgotHint` in DrillDatabaseApp.jsx).
forgotButton?.addEventListener("click", () => {
  if (forgotHint) forgotHint.hidden = false;
});

form?.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!emailInput || !passwordInput || !errorEl) return;

  const email = emailInput.value;
  const password = passwordInput.value;

  errorEl.hidden = true;
  errorEl.textContent = "";

  if (loginButton) {
    loginButton.disabled = true;
    loginButton.textContent = "Logging in...";
  }

  try {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      // Generic message regardless of 400 (malformed request) vs 401 (bad
      // credentials) — never distinguishes which field was wrong (anti-
      // enumeration requirement).
      errorEl.textContent = "Incorrect email or password.";
      errorEl.hidden = false;
      if (res.status === 401) {
        // Move focus to the password field and clear it — a credentials
        // problem, so the operator's next action is almost always
        // re-typing the password, not the email.
        passwordInput.value = "";
        passwordInput.focus();
      }
      if (loginButton) {
        loginButton.disabled = false;
        loginButton.textContent = "Log in";
      }
      return;
    }

    // Success: button stays disabled through the redirect — deliberate
    // exception to this file's own re-enable-on-settle pattern above, since
    // there's no "settled" state left to return the button to; the page is
    // about to navigate away.
    window.location.href = "/";
  } catch {
    // Network failure, not a credentials issue — password left untouched,
    // focus left untouched.
    errorEl.textContent = "Could not reach the server. Try again.";
    errorEl.hidden = false;
    if (loginButton) {
      loginButton.disabled = false;
      loginButton.textContent = "Log in";
    }
  }
});
